// ABOUTME: Changes the quantity of a cart line or removes it, with the snackbar feedback of an add: an
// ABOUTME: "updating…"/"removing…" toast replaced by success or error.

import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useCartContext } from './CartContext';

/**
 * Returns `updateQuantity` and `removeItem`, each resolving to true once the cart holds the change
 * and false when it failed or there is no cart. They never throw: failures are shown as a snackbar.
 */
export function useCartItemActions() {
    const { formatMessage } = useIntl();
    const cart = useCartContext();

    // Persistent until replaced, as an add's toast; duplicates allowed so each change gets its own.
    const withFeedback = useCallback(
        async (progress: string, done: string, failed: string, change: () => Promise<void>) => {
            const progressKey = SnackBarUtils.toast({
                msg: progress,
                variant: 'info',
                persist: true,
                preventDuplicate: false,
            });
            const replaceKey = progressKey || undefined;
            try {
                await change();
                SnackBarUtils.success(done, { replaceKey });
                return true;
            } catch (err) {
                SnackBarUtils.error(err instanceof Error && err.message ? err.message : failed, {
                    replaceKey,
                });
                return false;
            }
        },
        [],
    );

    const updateQuantity = useCallback(
        async (lineItemId: string, quantity: number) => {
            if (!cart) return false;
            return withFeedback(
                formatMessage({ id: 'cpq.cart.updating', defaultMessage: 'Updating quantity…' }),
                formatMessage({
                    id: 'cpq.cart.updated',
                    defaultMessage: 'Quantity updated in cart',
                }),
                formatMessage({
                    id: 'cpq.cart.update-error',
                    defaultMessage: 'Failed to update the quantity',
                }),
                () => cart.updateItem(lineItemId, { quantity }),
            );
        },
        [cart, formatMessage, withFeedback],
    );

    const removeItem = useCallback(
        async (lineItemId: string) => {
            if (!cart) return false;
            return withFeedback(
                formatMessage({
                    id: 'cpq.cart.removing',
                    defaultMessage: 'Removing product from cart…',
                }),
                formatMessage({
                    id: 'cpq.cart.removed',
                    defaultMessage: 'Product removed from cart',
                }),
                formatMessage({
                    id: 'cpq.cart.remove-error',
                    defaultMessage: 'Failed to remove the product',
                }),
                () => cart.removeItem(lineItemId),
            );
        },
        [cart, formatMessage, withFeedback],
    );

    return { updateQuantity, removeItem };
}
